// Ahoy event tracking utility for creator analytics
// Tracks events to /ahoy/events endpoint for server-side analytics

import { post } from "@rails/request.js"

type AhoyEventProperties = Record<
  string,
  string | number | boolean | null | undefined
>

interface AhoyEvent {
  name: string
  properties: AhoyEventProperties
  time?: string
}

// Track an event to Ahoy API
export async function trackAhoyEvent(
  name: string,
  properties: AhoyEventProperties = {},
): Promise<void> {
  // Only track in browser environment
  if (typeof window === "undefined") {
    return
  }

  try {
    // Get visit and visitor tokens from cookies or localStorage
    // Ahoy may store tokens in cookies (if enabled) or localStorage (if cookies disabled)
    let visitToken = getCookie("ahoy_visit")
    let visitorToken = getCookie("ahoy_visitor")

    // If not in cookies, try localStorage (Ahoy may store there when cookies are disabled)
    visitToken ??= localStorage.getItem("ahoy_visit") ?? null
    visitorToken ??= localStorage.getItem("ahoy_visitor") ?? null

    // If still no tokens, Ahoy will create a new visit automatically
    // We can send the request without tokens and Ahoy will handle it

    const event: AhoyEvent = {
      name,
      properties,
      time: new Date().toISOString(),
    }

    // Build payload - include tokens if available, otherwise let Ahoy create a new visit
    const payload: {
      visit_token?: string | null
      visitor_token?: string | null
      events: AhoyEvent[]
    } = {
      events: [event],
    }

    if (visitToken) {
      payload.visit_token = visitToken
    }
    if (visitorToken) {
      payload.visitor_token = visitorToken
    }

    const response = await post("/ahoy/events", {
      body: JSON.stringify(payload),
      contentType: "application/json",
    })

    if (!response.ok) {
      if (import.meta.env.DEV) {
        console.warn(`Ahoy event tracking failed: ${response.statusCode}`)
      }
    }
  } catch (error) {
    // Silently fail - don't break the app if analytics fails
    if (import.meta.env.DEV) {
      console.warn("Ahoy event tracking error:", error)
    }
  }
}

// Helper to get cookie value
function getCookie(name: string): string | null {
  if (typeof document === "undefined") {
    return null
  }

  const value = `; ${document.cookie}`
  const parts = value.split(`; ${name}=`)

  if (parts.length === 2) {
    return parts.pop()?.split(";").shift() ?? null
  }

  return null
}

// Track preview play event
export function trackPreviewPlay(
  itemId: string,
  spaceId: string,
  previewDurationSeconds: number,
): void {
  void trackAhoyEvent("preview", {
    item_id: itemId,
    space_id: spaceId,
    preview_duration_seconds: previewDurationSeconds,
    source: "web",
  })
}

// Track full content play event
export function trackPlay(
  itemId: string,
  spaceId: string,
  durationSeconds?: number,
  percentComplete?: number,
  source: "web" | "ios_app" = "web",
  playbackMode: "stream" | "offline" = "stream",
  containerId?: string,
): void {
  const properties: AhoyEventProperties = {
    item_id: itemId,
    space_id: spaceId,
    source,
    playback_mode: playbackMode,
  }

  if (containerId) {
    properties.container_id = containerId
  }

  if (durationSeconds !== undefined) {
    properties.duration_seconds = durationSeconds
  }

  if (percentComplete !== undefined) {
    properties.percent_complete = percentComplete
  }

  void trackAhoyEvent("play", properties)
}
